// Tinjauan kontribusi anime (/web/kontribusi-admin), khusus akun admin anime.
// Alur & API: discord-bot/contrib.js. Proses = bot mengunduh, membongkar
// arsip, menomori episode & membuat cuplikan; Simpan = file diupload ke
// folder anime dengan nama di tabel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, AppBar, Box, Button, Checkbox, Chip, CircularProgress, CssBaseline, IconButton, LinearProgress, Link, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import BlockIcon from '@mui/icons-material/Block';
import { BOT_API_URL } from './config.js';
import { botJson, getAccount, hasValidSession, withToken } from './api.js';
import { canUseAnime } from './anime-library';
import { FORMAT_LABELS } from './anilist';
import { formatSize } from './file-utils';

const ACCENT = '#ff7a1a';
const theme = createTheme({
    shape: { borderRadius: 10 },
    palette: { mode: 'dark', primary: { main: ACCENT, contrastText: '#000' }, background: { default: '#0e0e12', paper: '#18181f' } },
    typography: { button: { textTransform: 'none', fontWeight: 700 } },
    components: { MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } } },
});

const STATUS = {
    mengunggah: { label: 'Sedang diunggah', color: 'default' },
    baru: { label: 'Baru', color: 'warning' },
    diproses: { label: 'Diproses', color: 'info' },
    siap: { label: 'Siap ditinjau', color: 'primary' },
    menyimpan: { label: 'Menyimpan', color: 'info' },
    selesai: { label: 'Tayang', color: 'success' },
    ditolak: { label: 'Ditolak', color: 'error' },
    gagal: { label: 'Gagal', color: 'error' },
};
const RUNNING = new Set(['diproses', 'menyimpan']);

function StatusChip({ status }) {
    const s = STATUS[status] || { label: status, color: 'default' };
    return <Chip size="small" label={s.label} color={s.color} />;
}

function Poster({ url, width }) {
    return <Box sx={{ width, aspectRatio: '2 / 3', flexShrink: 0, borderRadius: 1.5, bgcolor: '#23232c', backgroundImage: url ? `url("${url}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />;
}

function Field({ label, children }) {
    return (
        <>
            <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600, wordBreak: 'break-word' }}>{children || '-'}</Typography>
        </>
    );
}

function Detail({ item, userId, onAction, busy }) {
    const [folder, setFolder] = useState('');
    const [rows, setRows] = useState([]);
    useEffect(() => {
        setFolder(item.plan ? item.plan.folder : '');
        setRows(item.plan ? item.plan.items.map((i) => ({ include: i.include, target: i.target })) : []);
    }, [item.code, item.plan]);
    const anime = item.anime || {};
    const job = item.job;
    const canProcess = ['baru', 'gagal', 'siap'].includes(item.status);
    const canSave = ['siap', 'gagal'].includes(item.status) && item.plan;
    const chosen = rows.filter((r) => r.include).length;
    const thumbs = Array.from({ length: item.thumbs || (item.plan && item.plan.thumbs) || 0 }, (_, n) => withToken(`${BOT_API_URL}/kontribusi/${userId}/${item.code}/thumb/${n}?v=${item.updatedAt}`));
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Poster url={anime.cover} width={110} />
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <StatusChip status={item.status} />
                        <Typography variant="caption" color="text.secondary">Kode {item.code} · {new Date(item.createdAt).toLocaleString('id-ID')}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 24, lineHeight: 1.2, mt: 0.75 }}>{anime.title}</Typography>
                    {anime.titleEnglish && anime.titleEnglish !== anime.title && <Typography color="text.secondary">{anime.titleEnglish}</Typography>}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {[anime.year, FORMAT_LABELS[anime.format], anime.episodes && `${anime.episodes} episode`].filter(Boolean).join(' · ')}
                        {' · '}<Link href={`https://anilist.co/anime/${anime.id}`} target="_blank" rel="noreferrer">AniList</Link>
                    </Typography>
                </Box>
            </Box>

            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.75 }}>
                <Field label="Kontributor">{item.name}</Field>
                <Field label="Kontak">{item.contact}</Field>
                <Field label="Season / episode">{[item.season, item.episodes].filter(Boolean).join(' · ')}</Field>
                <Field label="Subtitle / kualitas">{`${item.subLang} · ${item.quality} · ${item.source}`}</Field>
                <Field label="Password arsip">{item.archivePassword}</Field>
                <Field label={item.method === 'link' ? 'Link' : 'File'}>
                    {item.method === 'link'
                        ? item.links.map((l) => <Box key={l}><Link href={l} target="_blank" rel="noreferrer" sx={{ wordBreak: 'break-all' }}>{l}</Link></Box>)
                        : item.uploads.map((f) => <Box key={f.name}>{f.name} ({formatSize(f.size)}){f.done ? '' : ' (belum terupload)'}</Box>)}
                </Field>
                <Field label="Catatan">{item.note}</Field>
                {item.reviewNote && <Field label="Alasan ditolak">{item.reviewNote}</Field>}
                {item.result && <Field label="Tersimpan di">{`${item.result.folder} (${item.result.saved} file)`}</Field>}
            </Box>

            {item.error && <Alert severity="error">{item.error}</Alert>}
            {RUNNING.has(item.status) && job && (
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.75 }}>{job.message}</Typography>
                    <LinearProgress variant={job.total ? 'determinate' : 'indeterminate'} value={job.total ? Math.min(100, (job.bytes / job.total) * 100) : 0} sx={{ height: 8, borderRadius: 4 }} />
                    {job.total > 0 && <Typography variant="caption" color="text.secondary">{formatSize(job.bytes || 0)} dari {formatSize(job.total)}</Typography>}
                </Box>
            )}
            {item.plan && item.plan.warnings && item.plan.warnings.map((w) => <Alert key={w} severity="warning">{w}</Alert>)}

            {thumbs.length > 0 && (
                <Box>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Cuplikan episode pertama</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        {thumbs.map((src) => <Box key={src} component="img" src={src} alt="" sx={{ width: { xs: '100%', sm: 300 }, borderRadius: 2 }} />)}
                        <Poster url={anime.cover} width={110} />
                    </Box>
                </Box>
            )}

            {item.plan && (
                <Box>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>File yang akan disimpan</Typography>
                    <TextField fullWidth size="small" label="Folder tujuan" value={folder} onChange={(e) => setFolder(e.target.value)} disabled={!canSave} sx={{ mb: 1.5 }} />
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Nama asli</TableCell>
                                    <TableCell>Ep</TableCell>
                                    <TableCell sx={{ minWidth: 240 }}>Nama baru</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {item.plan.items.map((planItem, i) => (
                                    <TableRow key={planItem.source} sx={{ opacity: rows[i] && rows[i].include ? 1 : 0.5 }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox size="small" checked={Boolean(rows[i] && rows[i].include)} disabled={!canSave}
                                                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, include: e.target.checked } : r)))} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 280 }}>
                                            <Typography variant="body2" noWrap title={planItem.original}>{planItem.original}</Typography>
                                            <Typography variant="caption" color="text.secondary">{planItem.kind === 'subtitle' ? 'subtitle · ' : ''}{formatSize(planItem.size)}</Typography>
                                        </TableCell>
                                        <TableCell>{planItem.episode ?? '-'}</TableCell>
                                        <TableCell>
                                            <TextField fullWidth size="small" variant="standard" value={rows[i] ? rows[i].target : ''} disabled={!canSave}
                                                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, target: e.target.value } : r)))} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {canSave && (
                    <Button variant="contained" startIcon={<SaveIcon />} disabled={busy || chosen === 0} onClick={() => onAction('simpan', { folder, items: rows })}>
                        Simpan ke Disbox ({chosen} file)
                    </Button>
                )}
                {canProcess && (
                    <Button variant={canSave ? 'outlined' : 'contained'} startIcon={<PlayArrowIcon />} disabled={busy} onClick={() => onAction('proses')}>
                        {item.status === 'baru' ? 'Proses (unduh & bongkar)' : 'Proses ulang'}
                    </Button>
                )}
                {!RUNNING.has(item.status) && !['selesai', 'ditolak'].includes(item.status) && (
                    <Button color="error" variant="outlined" startIcon={<BlockIcon />} disabled={busy} onClick={() => {
                        const reason = window.prompt('Alasan ditolak (ditampilkan ke pengirim, boleh kosong):', '');
                        if (reason !== null) onAction('tolak', { reason });
                    }}>Tolak</Button>
                )}
                {!RUNNING.has(item.status) && (
                    <Tooltip title="Hapus dari daftar beserta file sementaranya">
                        <IconButton color="error" disabled={busy} onClick={() => { if (window.confirm('Hapus kiriman ini dari daftar?')) onAction('hapus'); }}><DeleteOutlineIcon /></IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

export default function ContributeAdmin() {
    const navigate = useNavigate();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const account = getAccount();
    const allowed = hasValidSession() && canUseAnime();
    const [items, setItems] = useState(null);
    const [selected, setSelected] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const userId = account && account.userId;

    const load = useCallback(async () => {
        try {
            const data = await botJson(`/kontribusi/${userId}`);
            setItems(data.items || []);
            setError(null);
        } catch (loadError) {
            setError(loadError.message);
        }
    }, [userId]);

    useEffect(() => {
        if (!allowed) {
            navigate('/setup?relogin=1', { replace: true });
            return;
        }
        document.title = 'Kontribusi | DisboxAnime';
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const anyRunning = (items || []).some((i) => RUNNING.has(i.status));
    useEffect(() => {
        if (!anyRunning) return undefined;
        const timer = setInterval(load, 3000);
        return () => clearInterval(timer);
    }, [anyRunning, load]);

    const current = useMemo(() => (items || []).find((i) => i.code === selected) || (items || [])[0] || null, [items, selected]);

    const onAction = async (action, body = {}) => {
        setBusy(true);
        try {
            if (action === 'hapus') {
                await botJson(`/kontribusi/${userId}/${current.code}`, { method: 'DELETE' });
                setSelected(null);
            } else {
                await botJson(`/kontribusi/${userId}/${current.code}/${action}`, { method: 'POST', json: body });
            }
            await load();
        } catch (actionError) {
            alert(actionError.message);
        } finally {
            setBusy(false);
        }
    };

    const waiting = (items || []).filter((i) => ['baru', 'siap', 'gagal'].includes(i.status)).length;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
                <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(14,14,18,.95)' }}>
                    <Toolbar sx={{ gap: 1 }}>
                        <IconButton color="inherit" onClick={() => navigate('/anime')} aria-label="Kembali"><ArrowBackIcon /></IconButton>
                        <LiveTvIcon sx={{ color: ACCENT }} />
                        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Kontribusi<Box component="span" sx={{ color: ACCENT }}> Anime</Box></Typography>
                        {waiting > 0 && <Chip size="small" color="warning" label={`${waiting} perlu ditinjau`} sx={{ ml: 1 }} />}
                        <Box sx={{ flex: 1 }} />
                        <IconButton color="inherit" onClick={load} aria-label="Muat ulang"><RefreshIcon /></IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 }, display: 'flex', gap: 3, flexDirection: isDesktop ? 'row' : 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ width: isDesktop ? 360 : '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {items === null && !error && <CircularProgress sx={{ m: 2 }} />}
                        {items && items.length === 0 && <Typography color="text.secondary">Belum ada kiriman. Halaman kontribusi publik: /web/kontribusi</Typography>}
                        {(items || []).map((item) => (
                            <Box key={item.code} onClick={() => setSelected(item.code)} sx={{
                                display: 'flex', gap: 1.5, p: 1.25, borderRadius: 2, cursor: 'pointer', bgcolor: 'background.paper',
                                border: '1px solid', borderColor: current && current.code === item.code ? ACCENT : 'transparent',
                            }}>
                                <Poster url={item.anime && item.anime.cover} width={44} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{item.anime && item.anime.title}</Typography>
                                    <Typography variant="caption" color="text.secondary" component="div" noWrap>{item.name} · {new Date(item.createdAt).toLocaleDateString('id-ID')}</Typography>
                                    <Box sx={{ mt: 0.5 }}><StatusChip status={item.status} /></Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%', bgcolor: 'background.paper', borderRadius: 3, p: { xs: 2, md: 3 } }}>
                        {current ? <Detail item={current} userId={userId} onAction={onAction} busy={busy} /> : <Typography color="text.secondary">Pilih kiriman di kiri.</Typography>}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
